import {
  LayoutDashboard,
  ClipboardCheck,
  FolderPlus,
  SquarePen,
  FileText,
  Users,
  Wallet,
  Video,
  ShieldCheck,
  Inbox,
  Settings,
  ListChecks,
  Sparkles,
  Handshake,
} from "lucide-react";

/**
 * The reference splits the client rail into labelled groups (client.tsx). The
 * old admin rail carried the same destinations as click-to-open dropdowns
 * (Blog / Talent / Client / Settings); each dropdown becomes one labelled
 * group here. Same paths, same labels — only the grouping and icons are new.
 *
 * `also` lists extra paths that should keep a nav item lit, so detail screens
 * do not blank out the rail.
 */
export const OVERVIEW_NAV = [
  { key: "dashboard", label: "Dashboard", path: "/", icon: LayoutDashboard },
  {
    key: "result",
    label: "Result",
    path: "/search-talent",
    icon: ClipboardCheck,
  },
  {
    key: "roles",
    label: "Role Library",
    path: "/cbt/roles",
    icon: ListChecks,
  },
  {
    key: "cbt-questions",
    label: "Generate Questions",
    path: "/cbt/questions",
    icon: Sparkles,
  },
];

export const TALENT_NAV = [
  {
    key: "talents",
    label: "View Talents",
    path: "/talents",
    icon: Users,
    also: ["/talent-incomplete-profile", "/incomplete-profile"],
  },
  { key: "payments", label: "Talent Payment", path: "/payments", icon: Wallet },
  {
    key: "creators",
    label: "Micro-Content Creator",
    path: "/micro-content/creator",
    icon: Video,
    also: ["/content-creator"],
  },
];

export const CLIENT_NAV = [
  {
    key: "kyb",
    label: "KYB Verification",
    path: "/client/kyb",
    icon: ShieldCheck,
  },
  {
    key: "client-request",
    label: "Client Request",
    path: "/client-request",
    icon: Inbox,
    also: ["/matched-talents", "/client-details", "/client/jobs/pending"],
  },
];

/* Schools, event hosts and individuals who bring talents and clients. */
export const PARTNER_NAV = [
  { key: "partners", label: "Partners", path: "/partners", icon: Handshake },
];

export const BLOG_NAV = [
  {
    key: "create-cat-aut",
    label: "Create Cat/Aut",
    path: "/createauthorcategory",
    icon: FolderPlus,
  },
  { key: "postblog", label: "Create Post", path: "/postblog", icon: SquarePen },
  {
    key: "mypost",
    label: "Blog Content",
    path: "/mypost",
    icon: FileText,
    also: ["/editpost"],
  },
];

export const SETTINGS_NAV = [
  {
    key: "settings",
    label: "Basic setting",
    path: "/settings",
    icon: Settings,
  },
];

export const NAV_GROUPS = [
  { label: "Overview", items: OVERVIEW_NAV },
  { label: "Client", items: CLIENT_NAV },
  { label: "Talent", items: TALENT_NAV },
  { label: "Partners", items: PARTNER_NAV },
  { label: "Blog", items: BLOG_NAV },
  { label: "Settings", items: SETTINGS_NAV },
];

/** Small labels for the sticky header, per the reference's path→label map. */
export const PAGE_TITLES = {
  "/": "Dashboard",
  "/search-talent": "Search Talent",
  "/search-talent/result": "Enter Result",
  "/talents": "Talents",
  "/payments": "Talent Payments",
  "/talent-incomplete-profile": "Incomplete Profiles",
  "/micro-content/creator": "Micro-Content Creators",
  "/client/kyb": "KYB Verification",
  "/client-request": "Client Requests",
  "/client/jobs/pending": "Pending Jobs",
  "/createauthorcategory": "Create Category / Author",
  "/postblog": "Create Post",
  "/mypost": "Blog Content",
  "/settings": "Settings",
  "/cbt/roles": "Role Library",
  "/cbt/questions": "Generate Questions",
  "/partners": "Partners",
};

/* The reference spells its dynamic segments out the same way (client.tsx:
   `if (path.startsWith("/client/team/")) return "Team Member"`). */
const DYNAMIC_TITLES = [
  [/^\/cbt\/questions\/[^/]+$/, "Question Run"],
  [/^\/matched-talents\/cbt-profile\/[^/]+$/, "CBT Profile"],
  [/^\/matched-talents\/[^/]+$/, "Matched Talents"],
  [/^\/client\/kyb\/[^/]+$/, "KYB Details"],
  [/^\/partners\/[^/]+$/, "Partner Details"],
  [/^\/client-details\/[^/]+$/, "Client Details"],
  [/^\/incomplete-profile\/[^/]+$/, "Incomplete Profile"],
  [/^\/content-creator\/[^/]+$/, "Creator Profile"],
  [/^\/editpost\/[^/]+$/, "Edit Post"],
  [/^\/mypost\/[^/]+$/, "Blog Post"],
];

export const pageTitleFor = (pathname) => {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];

  const dynamic = DYNAMIC_TITLES.find(([re]) => re.test(pathname));
  if (dynamic) return dynamic[1];

  const match = Object.keys(PAGE_TITLES)
    .filter((p) => p !== "/" && pathname.startsWith(p))
    .sort((a, b) => b.length - a.length)[0];
  return match ? PAGE_TITLES[match] : "Droomwork Admin";
};

/**
 * Reference active matcher: exact match, or a prefix match for anything that
 * is not the home route (so detail pages keep their parent lit).
 */
export const isNavItemActive = (item, pathname) => {
  if (pathname === item.path) return true;
  if (item.path !== "/" && pathname.startsWith(`${item.path}/`)) return true;
  return (item.also ?? []).some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
};

/** Header eyebrow — names the rail group the current page sits in. */
export const sectionFor = (pathname) => {
  const group = NAV_GROUPS.find((g) =>
    g.items.some((item) => isNavItemActive(item, pathname)),
  );
  return group ? group.label : "Droomwork";
};
